import { parseDisplayToCents } from "@/utils/fieldValueConverter";

const UNSUPPORTED_NSPD_FIELD_TYPES = new Set([
  "object",
  "template",
  "repository",
  "table",
  "employee",
  "file",
  "image",
  "parent_polygon",
  "rpoint",
]);

const NUMBER_NSPD_FIELD_TYPES = new Set(["float", "integer", "money"]);
const STRING_NSPD_FIELD_TYPES = new Set(["text", "mask"]);
const DATE_NSPD_FIELD_TYPES = new Set([
  "day",
  "month",
  "year",
  "moment",
  "date",
]);
const MAP_NSPD_FIELD_TYPES = new Set(["point", "polygon"]);

export const convertNspdValue = (rawValue, field) => {
  const type = field?.type;

  if (UNSUPPORTED_NSPD_FIELD_TYPES.has(type)) {
    return {
      ok: false,
      reason: "Тип поля не поддерживается для синхронизации с НСПД",
    };
  }

  if (rawValue === undefined || rawValue === null || rawValue === "") {
    return {
      ok: false,
      reason: "Связанное поле не найдено в ответе НСПД",
    };
  }

  try {
    if (NUMBER_NSPD_FIELD_TYPES.has(type)) {
      return convertToNumber(rawValue, type);
    }

    if (STRING_NSPD_FIELD_TYPES.has(type)) {
      return convertToText(rawValue);
    }

    if (DATE_NSPD_FIELD_TYPES.has(type)) {
      return convertToDateField(rawValue, type);
    }

    if (MAP_NSPD_FIELD_TYPES.has(type)) {
      return convertToMapField(rawValue, type);
    }

    return {
      ok: false,
      reason: "Неизвестный тип поля",
    };
  } catch {
    return {
      ok: false,
      reason: "Ошибка преобразования значения",
    };
  }
};

const convertToNumber = (rawValue, type) => {
  const normalized = String(rawValue).trim().replace(",", ".");

  if (!normalized) {
    return {
      ok: false,
      reason: "Пустое числовое значение",
    };
  }

  const numberRegexp =
    type === "integer" ? /^-?\d+$/ : /^-?(?:\d+|\d+\.\d+|\.\d+)$/;

  if (!numberRegexp.test(normalized)) {
    return {
      ok: false,
      reason:
        type === "integer"
          ? "Значение из НСПД не соответствует формату целого числа"
          : "Значение из НСПД не соответствует формату числа",
    };
  }

  const value = Number(normalized);

  if (!Number.isFinite(value)) {
    return {
      ok: false,
      reason: "Значение не удалось преобразовать в число",
    };
  }

  if (type === "money") {
    return { ok: true, value: parseDisplayToCents(value) };
  }

  return { ok: true, value };
};

const convertToText = (rawValue) => {
  if (Array.isArray(rawValue)) {
    const hasComplexValue = rawValue.some(
      (item) => item !== null && typeof item === "object",
    );

    if (hasComplexValue) {
      return {
        ok: false,
        reason: "Массив с объектами нельзя преобразовать в строку",
      };
    }

    return {
      ok: true,
      value: rawValue.filter(Boolean).map(String).join(", "),
    };
  }

  if (typeof rawValue === "object") {
    return {
      ok: false,
      reason: "Объект нельзя преобразовать в строку",
    };
  }

  return {
    ok: true,
    value: String(rawValue),
  };
};

const convertToDateField = (rawValue, type) => {
  const date = parseNspdDate(rawValue);

  if (!date) {
    return {
      ok: false,
      reason: "Значение не удалось преобразовать в дату",
    };
  }

  const isoDate = date.toISOString().slice(0, 10);

  switch (type) {
    case "moment":
      return { ok: true, value: date.toISOString() };

    case "date":
    case "day":
      return { ok: true, value: isoDate };

    case "month":
      return { ok: true, value: isoDate.slice(0, 7) };

    case "year":
      return { ok: true, value: String(date.getUTCFullYear()) };

    default:
      return {
        ok: false,
        reason: "Неизвестный тип даты",
      };
  }
};

const isStrictValidDate = (date, isoDate) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return false;
  }

  return date.toISOString().slice(0, 10) === isoDate;
};

const parseNspdDate = (rawValue) => {
  if (rawValue instanceof Date && !Number.isNaN(rawValue.getTime())) {
    return rawValue;
  }

  if (typeof rawValue !== "string") return null;

  const value = rawValue.trim();

  if (!value) return null;

  // 2014-01-23
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const date = new Date(`${value}T00:00:00.000Z`);
    return isStrictValidDate(date, value) ? date : null;
  }

  // 2014-01-23T00:00:00Z / 2014-01-23T00:00:00.000Z
  if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  // 23.01.2014
  const ruDateMatch = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);

  if (ruDateMatch) {
    const [, day, month, year] = ruDateMatch;
    const date = new Date(`${year}-${month}-${day}T00:00:00.000Z`);

    return isStrictValidDate(date, `${year}-${month}-${day}`) ? date : null;
  }

  return null;
};

const isCoordinatePair = (value) => {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    Number.isFinite(Number(value[0])) &&
    Number.isFinite(Number(value[1]))
  );
};

const isCoordinatePairsArray = (value) => {
  return (
    Array.isArray(value) && value.length >= 3 && value.every(isCoordinatePair)
  );
};

const convertToMapField = (rawValue, type) => {
  if (type === "point") {
    if (rawValue?.type && rawValue.type !== "Point") {
      return {
        ok: false,
        reason: `Ожидалась точка, но в НСПД пришла геометрия ${rawValue.type}`,
      };
    }

    const point = extractPoint(rawValue);

    if (!point) {
      return {
        ok: false,
        reason: "Значение не удалось преобразовать в точку",
      };
    }

    return { ok: true, value: point };
  }

  if (type === "polygon") {
    if (rawValue?.type && rawValue.type !== "Polygon") {
      return {
        ok: false,
        reason: `Ожидался полигон, но в НСПД пришла геометрия ${rawValue.type}`,
      };
    }

    const polygon = extractPolygon(rawValue);

    if (!polygon?.length) {
      return {
        ok: false,
        reason: "Значение не удалось преобразовать в полигон",
      };
    }

    return { ok: true, value: polygon };
  }

  return {
    ok: false,
    reason: "Тип карты не поддерживается для синхронизации с НСПД",
  };
};

const extractPoint = (rawValue) => {
  if (isValidLatLon(rawValue)) {
    return {
      lat: Number(rawValue.lat),
      lon: Number(rawValue.lon),
    };
  }

  if (
    rawValue?.type === "Point" &&
    Array.isArray(rawValue.coordinates) &&
    rawValue.coordinates.length >= 2
  ) {
    return convertCoordinatesPair(rawValue.coordinates);
  }

  if (isCoordinatePair(rawValue)) {
    return convertCoordinatesPair(rawValue);
  }

  return null;
};

const extractPolygon = (rawValue) => {
  if (Array.isArray(rawValue) && rawValue.every(isValidLatLon)) {
    return rawValue.map((point) => ({
      lat: Number(point.lat),
      lon: Number(point.lon),
    }));
  }

  if (
    rawValue?.type === "Polygon" &&
    Array.isArray(rawValue.coordinates) &&
    Array.isArray(rawValue.coordinates[0])
  ) {
    const outerRing = rawValue.coordinates[0];
    const points = outerRing.map(convertCoordinatesPair).filter(Boolean);

    return points.length >= 3 ? points : null;
  }

  if (isCoordinatePairsArray(rawValue)) {
    const points = rawValue.map(convertCoordinatesPair).filter(Boolean);

    return points.length >= 3 ? points : null;
  }

  return null;
};

const convertCoordinatesPair = (coordinates) => {
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;

  const x = Number(coordinates[0]);
  const y = Number(coordinates[1]);

  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

  if (Math.abs(x) <= 180 && Math.abs(y) <= 90) {
    return {
      lat: y,
      lon: x,
    };
  }

  return webMercatorToLatLon(x, y);
};

const webMercatorToLatLon = (x, y) => {
  const earthRadius = 6378137;

  const lon = (x / earthRadius) * (180 / Math.PI);
  const lat =
    (2 * Math.atan(Math.exp(y / earthRadius)) - Math.PI / 2) * (180 / Math.PI);

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;

  return { lat, lon };
};

const isValidLatLon = (value) => {
  if (!value || typeof value !== "object") return false;

  const lat = Number(value.lat);
  const lon = Number(value.lon);

  return (
    Number.isFinite(lat) &&
    Number.isFinite(lon) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lon) <= 180
  );
};
